import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Drawer, Typography, Button, List, ListItem, ListItemText, IconButton, Divider, TextField, Stack, Accordion, AccordionSummary, AccordionDetails, Chip, Paper, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';

import EditableTypography from '../../base_components/EditableTypography';
import FleetVehicleModal from './FleetVehicleModal';
import FleetOperationsModal from './FleetOperationsModal';
import FleetContextMenu from './FleetContextMenu';
import { AutoFleetGeneratorModal } from './auto-fleet-generator';
import ListItemButton from '@mui/material/ListItemButton';
import { AppContext, Point, Layer } from '../../../context/context';
import { getAddressById, getValidPoints } from '../utils/fleetMappers';
import { v4 as uuid4 } from 'uuid';
import { logEvent } from '../../../services/analytics';
import { OperatingPeriod, BreakPeriod, FleetPresetType } from '../../../types/simulation';
import { FLEET_PRESETS, applyFleetPreset, generateFleetWithPreset } from '../../../services/simulation/syntheticData';
import { exportFleetToExcel, exportFleetToCSV } from '../../../utils/fleetExportHelpers';
import {
  frostedDrawerPaperSx,
  drawerPrimaryActionSx,
  drawerSecondaryActionSx,
  drawerDestructiveActionSx,
  drawerTertiaryActionSx,
  segmentedControlButtonSx,
} from '../../../theme/componentStyles';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { type Theme } from '@mui/material/styles';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../utils/iconFallbacks';
import { opsColors, opsElevation } from '../../../theme/opsTokens';
import { upsertVehicleTags } from '../../../features/rulesEngine/api';
import { flattenFleetsToVehicleTagSeeds } from '../../../features/rulesEngine/fleetVehicleTags';

export interface Vehicle {
  id: string;
  name: string;
  standardSeats: number;
  wheelchairSeats: number;
  startLocation: string;
  endLocation: string;
  operatingHours: OperatingPeriod[];
  numberOfVehicles?: number;
  /**
   * Optional depot coordinates. Populated by flows that know the depot lat/lng
   * but use a human-readable label for `startLocation` (e.g. Postcode Stop
   * Assignment push-to-map, AutoFleetGenerator, AgentChat). `fleetMappers`
   * falls back to these when the `startLocation` string is not a registered
   * point id, preventing `{0,0}` shift addresses in the simulation payload.
   */
  startLocationCoordinates?: { lat: number; lng: number };
  /** See `startLocationCoordinates`. */
  endLocationCoordinates?: { lat: number; lng: number };
  /**
   * Human-readable route name for slot-driven flows (Postcode Stop Assignment /
   * ELC), e.g. "DAY 1". Distinct from `name` (which stays the vehicle rego so
   * the right physical vehicle is attributed by id): this drives the *route*
   * label in the Optimised Fixed Route Network export, carried client-side via
   * `shiftLabelRegistry` and stamped onto activities as `route_name`.
   */
  routeName?: string;
  /** Shift band (DAY/LATE/NIGHT) backing `routeName`; stamped as `shift_band`. */
  shiftBand?: string;
  /**
   * UAT vehicle ID - preserved when importing vehicles from UAT
   */
  uatVehicleId?: string;
  /**
   * Original UAT vehicle data - preserved for future reference and re-export
   */
  uatMetadata?: {
    originalId: string;
    originalName?: string;
    capacities?: any[];
    picture?: string;
    importedAt?: number; // Timestamp when imported
    importedFrom?: string; // Environment (UAT/Production)
    productId?: string; // Product/network ID
    serviceDate?: string; // Service date when imported
    shifts?: any[]; // Original shift data
  };
}

export interface Fleet {
  id: string;
  name: string;
  vehicles: Vehicle[];
  /**
   * Original UAT fleet data - preserved for future reference and re-export
   */
  uatMetadata?: {
    importedAt?: number; // Timestamp when imported
    importedFrom?: string; // Environment (UAT/Production)
    productId?: string; // Product/network ID
    serviceDate?: string; // Service date when imported
    sourceVehicleIds?: string[]; // Original UAT vehicle IDs that were imported
  };
}

export interface FleetMenuProps {
  open: boolean;
  onClose: () => void;
  fleets: Fleet[];
  setFleets: React.Dispatch<React.SetStateAction<Fleet[]>>;
  rides?: any[]; // Optional rides data for intelligent generation
  selectedFleetPresets?: FleetPresetType[];
  onFleetPresetSelectionChange?: (selectedPresets: FleetPresetType[]) => void;
  /**
   * External trigger to open the Fleet Operations (timeline) modal for a specific fleet.
   * The drawer does NOT have to be open — the modal portals to document.body and works standalone.
   * Bump `nonce` to fire the trigger again for the same fleet.
   */
  editFleetTrigger?: { fleetId: string; nonce: number } | null;
}

const initialFleets: Fleet[] = [
  {
    id: 'fleet-1',
    name: 'Fleet 1',
    vehicles: [],
  },
];

// Excel/CSV parsing functions for fleet import
interface VehicleRowData {
  'Vehicle Name'?: string;
  'Vehicles Name'?: string;
  'Brand'?: string;
  'Vehicle Type'?: string;
  'Capacity (Standard)'?: number;
  'Capacity (Wheelchair)'?: number;
  'Number of Vehicles'?: number;
  'Shift #'?: number;
  'Shift Start'?: string;
  'Shift End'?: string;
  'Start Lat'?: number;
  'Start Lon'?: number;
  'End Lat'?: number;
  'End Lon'?: number;
  'Break #'?: number;
  'Break Start'?: string;
  'Break End'?: string;
  'Break Duration (s)'?: number;
  'Break Lat'?: number;
  'Break Lon'?: number;
}

/** Normalize key for matching (lowercase, trim) so we can match Excel headers with different casing/spacing. */
function normKey(k: string): string {
  return String(k).trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Get numeric value from row using first matching key; also tries case-insensitive match on row keys (supports alternate column names from Excel/CSV). */
function getNum(row: Record<string, unknown>, ...keys: string[]): number | undefined {
  const tryVal = (v: unknown): number | undefined => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isNaN(n) ? undefined : n;
  };
  for (const k of keys) {
    const v = row[k];
    const n = tryVal(v);
    if (n !== undefined) return n;
  }
  const rowKeysNorm = new Map<string, string>();
  for (const rk of Object.keys(row)) {
    rowKeysNorm.set(normKey(rk), rk);
  }
  for (const k of keys) {
    const rk = rowKeysNorm.get(normKey(k));
    if (rk !== undefined) {
      const n = tryVal(row[rk]);
      if (n !== undefined) return n;
    }
  }
  return undefined;
}

/** Terms that mean "start" location (depot, origin, etc.) for fuzzy column matching. */
const START_TERMS = ['start', 'origin', 'depot', 'from', 'begin'];

/**
 * Find first row key that matches a pattern (start/end + lat/lon).
 * e.g. matchPattern(row, 'start', 'lat') finds keys containing "start"/"origin"/"depot"/"from" and "lat" (e.g. "Start Lat", "Origin Latitude", "Depot Lat").
 * For lon we also accept "lng", "long", "longitude".
 */
function findKeyByPattern(row: Record<string, unknown>, startOrEnd: 'start' | 'end', latOrLon: 'lat' | 'lon'): string | undefined {
  const startEndTerms = startOrEnd === 'start' ? START_TERMS : ['end', 'to', 'finish'];
  const latLonParts = latOrLon === 'lat' ? ['lat', 'latitude'] : ['lon', 'lng', 'long', 'longitude'];
  for (const key of Object.keys(row)) {
    const n = normKey(key);
    const hasStartEnd = startEndTerms.some(term => n.includes(term));
    const hasLatLon = latLonParts.some(part => n.includes(part));
    if (hasStartEnd && hasLatLon) return key;
  }
  return undefined;
}

/** Get string value from row using first matching key (supports "Vehicle Name", "Vehicles Name", and case-insensitive match). */
function getVehicleNameFromRow(row: Record<string, unknown>): string | undefined {
  const keys = ['Vehicle Name', 'Vehicles Name', 'VehicleName', 'VehiclesName'];
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null && v !== '') return String(v).trim();
  }
  const rowKeysNorm = new Map<string, string>();
  for (const rk of Object.keys(row)) {
    rowKeysNorm.set(normKey(rk), rk);
  }
  for (const k of keys) {
    const rk = rowKeysNorm.get(normKey(k));
    if (rk !== undefined) {
      const v = row[rk];
      if (v !== undefined && v !== null && v !== '') return String(v).trim();
    }
  }
  return undefined;
}

/** Get numeric value for start/end + lat/lon using explicit keys then fuzzy header match (any column name containing start/end + lat/lon). */
function getLocationCoord(row: Record<string, unknown>, startOrEnd: 'start' | 'end', latOrLon: 'lat' | 'lon'): number | undefined {
  const explicitKeys = startOrEnd === 'start'
    ? (latOrLon === 'lat' ? ['Start Lat', 'Start Latitude', 'StartLat', 'start_lat', 'Origin Lat', 'Origin Latitude', 'Depot Lat', 'Depot Latitude', 'From Lat', 'From Latitude'] : ['Start Lon', 'Start Longitude', 'Start Lon.', 'StartLon', 'start_lon', 'Origin Lon', 'Origin Longitude', 'Depot Lon', 'Depot Longitude', 'From Lon', 'From Longitude'])
    : (latOrLon === 'lat' ? ['End Lat', 'End Latitude', 'EndLat', 'end_lat'] : ['End Lon', 'End Longitude', 'End Lon.', 'EndLon', 'end_lon']);
  const n = getNum(row, ...explicitKeys);
  if (n !== undefined) return n;
  const key = findKeyByPattern(row, startOrEnd, latOrLon);
  if (key === undefined) return undefined;
  const v = row[key];
  if (v === undefined || v === null || v === '') return undefined;
  const num = typeof v === 'number' ? v : Number(v);
  return Number.isNaN(num) ? undefined : num;
}

/** Options for Excel/CSV fleet parsing. When getPointId is provided and rows have Start/End Lat/Lon, vehicle start/end locations are set from the first row with valid coordinates per vehicle. */
export interface ParseVehicleDataOptions {
  getPointId?: (lat: number, lng: number, name: string) => string;
}

/**
 * Parses rows into a single Fleet. Rows with the same Vehicle Name are merged into one vehicle
 * with multiple operating periods (shifts). Capacities and Number of Vehicles come from the first
 * row per vehicle. When options.getPointId is provided, start/end locations are set from the
 * first row with valid Start/End Lat/Lon per vehicle (or same depot for both when only end is found).
 */
function parseVehicleData(data: VehicleRowData[], options?: ParseVehicleDataOptions): Fleet {
  const vehicleMap = new Map<string, Vehicle>();
  const baseId = `imported-${Date.now()}`;
  let vehicleIndex = 0;
  let fleetNameFromColumn: string | undefined;

  const isValidLat = (v: unknown): v is number =>
    typeof v === 'number' && !Number.isNaN(v) && v >= -90 && v <= 90;
  const isValidLon = (v: unknown): v is number =>
    typeof v === 'number' && !Number.isNaN(v) && v >= -180 && v <= 180;

  data.forEach((row, index) => {
    // Snapshot this row so we always read this row's values (avoids parsers reusing same object)
    const rowSnapshot = { ...row } as Record<string, unknown>;
    const nameFromColumn = getVehicleNameFromRow(rowSnapshot);
    // Skip empty rows (no vehicle/vehicles name and no other data)
    if (!nameFromColumn && !Object.values(rowSnapshot).some(v => v !== undefined && v !== '')) {
      return;
    }
    // Use first non-empty Vehicles Name / Vehicle Name for fleet name
    if (fleetNameFromColumn === undefined && nameFromColumn) {
      fleetNameFromColumn = nameFromColumn;
    }
    const vehicleName = nameFromColumn || `Imported Vehicle ${index + 1}`;
    // Merge rows by Vehicle Name: same name = one vehicle with multiple operating periods
    const groupKey = vehicleName.trim().toLowerCase().replace(/\s+/g, '-');

    if (!vehicleMap.has(groupKey)) {
      const vehicleId = `${baseId}-${vehicleIndex++}-${vehicleName.replace(/\s+/g, '-').toLowerCase()}`;
      vehicleMap.set(groupKey, {
        id: vehicleId,
        name: vehicleName,
        standardSeats: Number(rowSnapshot['Capacity (Standard)']) || 0,
        wheelchairSeats: Number(rowSnapshot['Capacity (Wheelchair)']) || 0,
        startLocation: '',
        endLocation: '',
        operatingHours: [],
        numberOfVehicles: Number(rowSnapshot['Number of Vehicles']) || 1
      });
    }

    const vehicle = vehicleMap.get(groupKey)!;

    // Set start/end from this row's coordinates only if not yet set (first row with valid coords wins)
    if (options?.getPointId) {
      const startLat = getLocationCoord(rowSnapshot, 'start', 'lat');
      const startLon = getLocationCoord(rowSnapshot, 'start', 'lon');
      const endLat = getLocationCoord(rowSnapshot, 'end', 'lat');
      const endLon = getLocationCoord(rowSnapshot, 'end', 'lon');
      if (!vehicle.startLocation && startLat !== undefined && isValidLat(startLat) && startLon !== undefined && isValidLon(startLon)) {
        vehicle.startLocation = options.getPointId(startLat, startLon, `${vehicleName} start`);
      }
      if (!vehicle.endLocation && endLat !== undefined && isValidLat(endLat) && endLon !== undefined && isValidLon(endLon)) {
        vehicle.endLocation = options.getPointId(endLat, endLon, `${vehicleName} end`);
      }
      if (!vehicle.startLocation && vehicle.endLocation) {
        vehicle.startLocation = vehicle.endLocation;
      }
    }

    // Handle shifts (use snapshot so we read this row's values)
    const shiftStart = rowSnapshot['Shift Start'];
    const shiftEnd = rowSnapshot['Shift End'];

    if (shiftStart && shiftEnd) {
      const parseTime = (timeStr: string): string => {
        if (typeof timeStr !== 'string') return '';
        if (timeStr.includes('T')) {
          return timeStr.split('T')[1]?.split('.')[0] || timeStr;
        }
        if (timeStr.includes(' ')) {
          return timeStr.split(' ')[1] || timeStr;
        }
        if (timeStr.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
          return timeStr.length === 5 ? timeStr : timeStr.substring(0, 5);
        }
        return timeStr;
      };

      const operatingPeriod: OperatingPeriod = {
        start: parseTime(String(shiftStart)),
        end: parseTime(String(shiftEnd)),
        breaks: []
      };

      const breakStart = rowSnapshot['Break Start'];
      const breakEnd = rowSnapshot['Break End'];

      if (breakStart && breakEnd) {
        const breakPeriod: BreakPeriod = {
          start: parseTime(String(breakStart)),
          end: parseTime(String(breakEnd)),
          location: ''
        };
        operatingPeriod.breaks = [breakPeriod];
      }

      const existingPeriod = vehicle.operatingHours.find(
        period => period.start === operatingPeriod.start && period.end === operatingPeriod.end
      );

      if (!existingPeriod) {
        vehicle.operatingHours.push(operatingPeriod);
      }
    }
  });

  const vehicles = Array.from(vehicleMap.values());
  const fleetName = fleetNameFromColumn
    ? `${fleetNameFromColumn.trim()} Fleet`
    : `Imported Fleet ${new Date().toLocaleDateString()}`;

  return {
    id: `imported-fleet-${Date.now()}`,
    name: fleetName,
    vehicles
  };
}

function parseExcelFile(file: File, options?: ParseVehicleDataOptions): Promise<Fleet> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as VehicleRowData[];

        const fleet = parseVehicleData(jsonData, options);
        resolve(fleet);
      } catch (error) {
        reject(new Error(`Error parsing Excel file: ${error}`));
      }
    };
    reader.onerror = () => reject(new Error('Error reading file'));
    reader.readAsArrayBuffer(file);
  });
}

function parseCSVFile(file: File, options?: ParseVehicleDataOptions): Promise<Fleet> {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        try {
          if (results.errors.length > 0) {
            console.warn('CSV parsing warnings:', results.errors);
          }
          const fleet = parseVehicleData(results.data as VehicleRowData[], options);
          resolve(fleet);
        } catch (error) {
          reject(new Error(`Error parsing CSV file: ${error}`));
        }
      },
      error: (error) => {
        reject(new Error(`Error reading CSV file: ${error}`));
      }
    });
  });
}

// Mapping function: UI Fleet -> API Fleet
function mapFleetToApi(fleet: Fleet, allPoints: { id: string; name: string; coordinates?: [number, number] }[]) {
  return {
    id: fleet.id,
    fleet_name: fleet.name,
    vehicles: fleet.vehicles.map(vehicle => ({
      capacity: [
        { type: 'standard', number: vehicle.standardSeats },
        { type: 'wheelchair', number: vehicle.wheelchairSeats }
      ],
      brand: 'Default',
      vehicle_type: 'Default',
      shifts: vehicle.operatingHours.map(period => ({
        start_address: getAddressById(vehicle.startLocation, allPoints),
        end_address: getAddressById(vehicle.endLocation, allPoints),
        start_time: period.start,
        end_time: period.end,
        breaks: period.breaks?.map(b => ({
          duration_in_seconds: b.start && b.end ? Math.max(0, Math.floor((new Date(b.end).getTime() - new Date(b.start).getTime()) / 1000)) : 0,
          start_time: b.start,
          end_time: b.end,
          break_addresses: b.location ? [getAddressById(b.location, allPoints)] : []
        }))
      }))
    }))
  };
}

const FleetMenu: React.FC<FleetMenuProps> = ({
  open,
  onClose,
  fleets,
  setFleets,
  rides = [],
  selectedFleetPresets = [],
  onFleetPresetSelectionChange,
  editFleetTrigger,
}) => {
  const { t } = useTranslation(['mapCore', 'common']);
  const navigate = useNavigate();
  const [expandedFleetId, setExpandedFleetId] = useState<string>(fleets[0]?.id || '');
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
  const [newFleetName, setNewFleetName] = useState('');
  const [selectedFleetId, setSelectedFleetId] = useState<string>('fleet-1');
  const [syncingToRostering, setSyncingToRostering] = useState(false);
  const context = useContext(AppContext);
  const mapLayers = context?.global?.mapLayers || [];
  const allPoints = useMemo(() => {
    return mapLayers.flatMap(layer =>
      layer.shapes.filter(s => s.type === 'point').map(p => ({ id: p.id, name: p.name || p.id }))
    );
  }, [mapLayers]);
  const [operationsModalFleetId, setOperationsModalFleetId] = useState<string | null>(null);
  const [returnToOperationsFleetId, setReturnToOperationsFleetId] = useState<string | null>(null);
  const [exportDialogFleetId, setExportDialogFleetId] = useState<string | null>(null);
  const [fleetContextMenu, setFleetContextMenu] = useState<{ x: number; y: number; fleet: Fleet } | null>(null);

  // External "Edit Fleet" trigger (e.g. from SimulationModal). Opens the operations modal
  // for the specified fleet without requiring the drawer itself to be open.
  useEffect(() => {
    if (!editFleetTrigger) return;
    setOperationsModalFleetId(editFleetTrigger.fleetId);
    setExpandedFleetId(editFleetTrigger.fleetId);
    setSelectedFleetId(editFleetTrigger.fleetId);
    logEvent({
      category: 'Fleet Management',
      action: 'FleetOperationsOpened',
      label: fleets.find(f => f.id === editFleetTrigger.fleetId)?.name || 'Unknown Fleet',
      fleet_id: editFleetTrigger.fleetId,
      source: 'simulation_modal',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFleetTrigger?.nonce]);
  const [showPresetSection, setShowPresetSection] = useState(false);
  const [showBulkOperationsSection, setShowBulkOperationsSection] = useState(false);
  const [autoFleetGeneratorOpen, setAutoFleetGeneratorOpen] = useState(false);
  const drawerCardSx = (theme: Theme) => ({
    borderRadius: 2,
    overflow: 'hidden',
    border: `1px solid ${opsColors.neutral.line}`,
    boxShadow: opsElevation.raised.shadow,
    '&:before': { display: 'none' },
    backgroundColor: opsColors.neutral.panel,
    '&.Mui-expanded': { margin: '0 0 14px 0' },
  });

  // Fleet preset toggle functionality
  const toggleFleetPreset = (presetType: FleetPresetType) => {
    if (!onFleetPresetSelectionChange) return;
    
    const newSelectedPresets = selectedFleetPresets.includes(presetType)
      ? selectedFleetPresets.filter(p => p !== presetType)
      : [...selectedFleetPresets, presetType];
    
    onFleetPresetSelectionChange(newSelectedPresets);
  };

  // Generate fleet with preset
  const generateFleetWithPresetHandler = (presetType: FleetPresetType) => {
    if (rides.length === 0) {
      alert(t('fleetMenu.presets.noRidesAlert'));
      return;
    }

    try {
      const presetFleet = generateFleetWithPreset(rides, presetType);
      const newFleet: Fleet = {
        id: `fleet-${Date.now()}`,
        name: t('fleetMenu.presets.fleetSuffix', { name: FLEET_PRESETS.find(p => p.type === presetType)?.name }),
        vehicles: presetFleet.vehicles.map(vs => ({
          id: `vehicle-${Date.now()}-${Math.random()}`,
          name: `${vs.vehicle.brand} ${vs.vehicle.vehicle_type}`,
          standardSeats: vs.vehicle.capacity.find(c => c.type === 'standard')?.number || 0,
          wheelchairSeats: vs.vehicle.capacity.find(c => c.type === 'wheelchair')?.number || 0,
          startLocation: '',
          endLocation: '',
          operatingHours: vs.vehicle.shifts.map(shift => ({
            start: shift.start_time.split(' ')[1] || '06:00',
            end: shift.end_time.split(' ')[1] || '22:00',
            breaks: []
          })),
          numberOfVehicles: vs.number_of_vehicles
        }))
      };
      
      setFleets([...fleets, newFleet]);
      setExpandedFleetId(newFleet.id);
      
      logEvent({
        category: 'Fleet Management',
        action: 'PresetFleetGenerated',
        label: presetType,
        fleet_id: newFleet.id,
        vehicle_count: newFleet.vehicles.length
      });
    } catch (error) {
      console.error('Error generating preset fleet:', error);
      alert(t('fleetMenu.presets.generateErrorAlert'));
    }
  };

  // Bulk operations and data import/export
  const exportFleetToJSON = (fleet: Fleet) => {
    const dataStr = JSON.stringify(fleet, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `${fleet.name.replace(/\s+/g, '_')}_fleet.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    logEvent({
      category: 'Fleet Management',
      action: 'FleetExported',
      label: fleet.name,
      fleet_id: fleet.id
    });
  };

  const exportAllFleetsToJSON = () => {
    const dataStr = JSON.stringify(fleets, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `all_fleets_${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
    
    logEvent({
      category: 'Fleet Management',
      action: 'AllFleetsExported',
      fleet_count: fleets.length
    });
  };

  /**
   * Helper function to get or create a point at coordinates
   * Returns the point ID (either existing or newly created)
   * If pointsToCreate is provided, adds the point to that array instead of immediately creating it
   */
  const getOrCreatePointAtCoordinates = (
    lat: number, 
    lng: number, 
    name: string,
    pointsToCreate?: Array<{ point: Point; layerId: number }>
  ): string => {
    if (!context?.global?.mapLayers || !context.global.setLayers) {
      console.warn('[Fleet Import] Context not available, falling back to coordinate string');
      return `${lat},${lng}`;
    }

    // Check if a point already exists at this location (within small tolerance)
    const allPoints = context.global.mapLayers.flatMap(layer =>
      layer.shapes.filter(s => s.type === 'point')
    ) as Point[];
    
    // Also check points that are queued to be created
    const queuedPoints = pointsToCreate?.map(({ point }) => point) || [];
    const allPointsToCheck = [...allPoints, ...queuedPoints];
    
    const tolerance = 0.0001; // ~11 meters
    const existingPoint = allPointsToCheck.find((p) => {
      const dist = Math.sqrt(
        Math.pow(p.coordinates[0] - lat, 2) + 
        Math.pow(p.coordinates[1] - lng, 2)
      );
      return dist < tolerance;
    });

    if (existingPoint) {
      console.log(`[Fleet Import] Reusing existing point "${existingPoint.name}" (${existingPoint.id}) at ${lat}, ${lng}`);
      return existingPoint.id;
    }

    // Create a new point at the location with unique ID
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    const pointId = `fleet-location-${timestamp}-${random}`;
    const targetLayer = context.global.mapLayers[0] || context.global.mapLayers.find(l => l.layerId === context.global.activeLayerId);
    
    if (!targetLayer) {
      console.warn('[Fleet Import] No target layer found, falling back to coordinate string');
      return `${lat},${lng}`; // Fallback if no layer exists
    }

    const newPoint: Point = {
      id: pointId,
      type: 'point',
      name: name || `Location ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      description: `Auto-generated location point for fleet import`,
      colour: '#1976d2',
      icon: 'location',
      size: 24,
      isLocked: false,
      coordinates: [lat, lng],
      layerId: targetLayer.layerId,
      hidden: false,
    };

    console.log(`[Fleet Import] Queueing new point "${newPoint.name}" (${pointId}) at ${lat}, ${lng}`);

    // If pointsToCreate array is provided, add to it for batch creation
    // Otherwise, create immediately (for backward compatibility)
    if (pointsToCreate) {
      pointsToCreate.push({ point: newPoint, layerId: targetLayer.layerId });
    } else {
      // Add point to the layer immediately
      const updatedLayer: Layer = {
        ...targetLayer,
        shapes: [...targetLayer.shapes, newPoint],
      };

      context.global.setLayers((prev: Layer[]) =>
        prev.map(layer => layer.layerId === targetLayer.layerId ? updatedLayer : layer)
      );
    }

    return pointId;
  };

  /**
   * Convert backend API format (create_vehicles) to frontend Fleet format
   */
  const convertBackendFormatToFleet = (backendData: any): Fleet | null => {
    try {
      // Check if it's the backend API format
      if (backendData.create_vehicles) {
        const createVehicles = backendData.create_vehicles;
        const vehiclesArray = createVehicles.vehicles?.vehicles || [];
        const fleetName = createVehicles.vehicles_name || 'Imported Fleet';
        
        // Get layer name for point naming
        const targetLayer = context?.global.mapLayers[0] || 
                           context?.global.mapLayers.find(l => l.layerId === context.global.activeLayerId);
        const layerName = targetLayer?.layerName || 'Default Layer';
        
        // Collect all points to create in a batch
        const pointsToCreate: Array<{ point: Point; layerId: number }> = [];
        
        // Convert backend vehicle format to frontend Vehicle format
        const convertedVehicles: Vehicle[] = [];
        
        // Track break location index across all vehicles for sequential numbering
        let globalBreakLocationIndex = 0;
        
        vehiclesArray.forEach((backendVehicle: any, index: number) => {
          const vehicleData = backendVehicle.vehicle;
          const numberOfVehicles = backendVehicle.number_of_vehicles || 1;
          
          // Extract capacity
          const capacity = vehicleData.capacity || [];
          const standardSeats = capacity.find((c: any) => c.type === 'standard')?.number || 0;
          const wheelchairSeats = capacity.find((c: any) => c.type === 'wheelchair')?.number || 0;
          
          // Helper function to extract time from datetime string
          const extractTime = (datetimeStr: string): string => {
            if (!datetimeStr) return '';
            // Handle formats like "2025-08-14 09:15:00" or "2025-08-14T09:15:00"
            if (datetimeStr.includes(' ')) {
              const timePart = datetimeStr.split(' ')[1];
              return timePart ? timePart.substring(0, 5) : ''; // Extract HH:mm
            } else if (datetimeStr.includes('T')) {
              const timePart = datetimeStr.split('T')[1];
              return timePart ? timePart.substring(0, 5) : ''; // Extract HH:mm
            } else if (datetimeStr.match(/^\d{1,2}:\d{2}(:\d{2})?$/)) {
              // Already in time format
              return datetimeStr.length === 5 ? datetimeStr : datetimeStr.substring(0, 5);
            }
            return datetimeStr;
          };
          
          // Convert shifts to operating hours
          const shifts = vehicleData.shifts || [];
          const operatingHours: OperatingPeriod[] = shifts.map((shift: any) => {
            const startAddress = shift.start_address || {};
            const endAddress = shift.end_address || {};
            
            // Convert breaks - extract time from datetime strings and location
            const breaks: BreakPeriod[] = (shift.breaks || []).map((br: any, breakIndex: number) => {
              // Extract break location from break_addresses array
              // Use first valid address with both latitude and longitude
              let breakLocation = '';
              if (br.break_addresses && Array.isArray(br.break_addresses) && br.break_addresses.length > 0) {
                const firstAddress = br.break_addresses.find((addr: any) => 
                  addr && typeof addr.latitude === 'number' && typeof addr.longitude === 'number'
                );
                if (firstAddress) {
                  globalBreakLocationIndex++;
                  // Create or get point for break location with correct naming convention
                  breakLocation = getOrCreatePointAtCoordinates(
                    firstAddress.latitude,
                    firstAddress.longitude,
                    `${layerName} Break Location #${globalBreakLocationIndex}`,
                    pointsToCreate
                  );
                  console.log(`[Fleet Import] Break location created: ${breakLocation} for break #${globalBreakLocationIndex}`);
                }
              }
              
              return {
                start: extractTime(br.start_time || ''),
                end: extractTime(br.end_time || ''),
                location: breakLocation
              };
            });
            
            return {
              start: extractTime(shift.start_time || ''),
              end: extractTime(shift.end_time || ''),
              breaks,
              serviceType: 'on-demand' as const
            };
          });
          
          // Get start and end locations from first shift (or use first break location as fallback)
          const firstShift = shifts[0] || {};
          const startAddress = firstShift.start_address || {};
          const endAddress = firstShift.end_address || {};
          
          // Extract start location - check if latitude and longitude are valid numbers
          // Create or get point for start location with correct naming convention
          let startLocation = '';
          if (startAddress && typeof startAddress.latitude === 'number' && typeof startAddress.longitude === 'number') {
            startLocation = getOrCreatePointAtCoordinates(
              startAddress.latitude,
              startAddress.longitude,
              `${layerName} Depot`,
              pointsToCreate
            );
            console.log(`[Fleet Import] Start location created: ${startLocation} for vehicle ${vehicleData.brand || 'Vehicle'}`);
          } else if (operatingHours[0]?.breaks?.[0]?.location) {
            startLocation = operatingHours[0].breaks[0].location;
            console.log(`[Fleet Import] Using break location as start location: ${startLocation}`);
          }
          
          // Extract end location - check if latitude and longitude are valid numbers
          // Create or get point for end location with correct naming convention
          let endLocation = '';
          if (endAddress && typeof endAddress.latitude === 'number' && typeof endAddress.longitude === 'number') {
            endLocation = getOrCreatePointAtCoordinates(
              endAddress.latitude,
              endAddress.longitude,
              `${layerName} Depot`,
              pointsToCreate
            );
            console.log(`[Fleet Import] End location created: ${endLocation} for vehicle ${vehicleData.brand || 'Vehicle'}`);
          } else if (operatingHours[0]?.breaks?.[0]?.location) {
            endLocation = operatingHours[0].breaks[0].location;
            console.log(`[Fleet Import] Using break location as end location: ${endLocation}`);
          }
          
          // Create vehicle for each instance (number_of_vehicles)
          for (let i = 0; i < numberOfVehicles; i++) {
            const vehicleId = `imported-${Date.now()}-${index}-${i}`;
            const vehicleName = `${vehicleData.brand || 'Vehicle'} ${numberOfVehicles > 1 ? i + 1 : ''}`.trim();
            
            console.log(`[Fleet Import] Creating vehicle "${vehicleName}" (${vehicleId})`);
            console.log(`[Fleet Import]   - Start Location: ${startLocation}`);
            console.log(`[Fleet Import]   - End Location: ${endLocation}`);
            console.log(`[Fleet Import]   - Operating Hours: ${operatingHours.length} period(s)`);
            operatingHours.forEach((period, pIdx) => {
              console.log(`[Fleet Import]     Period ${pIdx + 1}: ${period.start} - ${period.end}, ${period.breaks?.length || 0} break(s)`);
              period.breaks?.forEach((br, bIdx) => {
                console.log(`[Fleet Import]       Break ${bIdx + 1}: ${br.start} - ${br.end}, Location: ${br.location}`);
              });
            });
            
            convertedVehicles.push({
              id: vehicleId,
              name: vehicleName,
              standardSeats,
              wheelchairSeats,
              startLocation,
              endLocation,
              operatingHours,
              numberOfVehicles: 1
            });
          }
        });
        
        // Batch create all points at once
        if (pointsToCreate.length > 0 && context?.global.setLayers) {
          console.log(`[Fleet Import] Batch creating ${pointsToCreate.length} points`);
          context.global.setLayers((prev: Layer[]) => {
            const updatedLayers = [...prev];
            pointsToCreate.forEach(({ point, layerId }) => {
              const layerIndex = updatedLayers.findIndex(l => l.layerId === layerId);
              if (layerIndex >= 0) {
                updatedLayers[layerIndex] = {
                  ...updatedLayers[layerIndex],
                  shapes: [...updatedLayers[layerIndex].shapes, point],
                };
              }
            });
            return updatedLayers;
          });
        }
        
        return {
          id: `fleet-${Date.now()}-${Math.random()}`,
          name: fleetName,
          vehicles: convertedVehicles
        };
      }
      
      return null;
    } catch (error) {
      console.error('Error converting backend format:', error);
      return null;
    }
  };

  /** Process a single JSON fleet file and return fleets to add (for unified import). */
  const processJsonFleetFile = useCallback(
    (file: File): Promise<Fleet[]> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const importedData = JSON.parse(e.target?.result as string);

            if (Array.isArray(importedData) && importedData.length > 0) {
              for (const item of importedData) {
                if (item && typeof item === 'object' && item.create_vehicles) {
                  const convertedFleet = convertBackendFormatToFleet({ create_vehicles: item.create_vehicles });
                  if (convertedFleet) {
                    resolve([convertedFleet]);
                    return;
                  }
                }
              }
            }

            const convertedFleet = convertBackendFormatToFleet(importedData);
            if (convertedFleet) {
              resolve([convertedFleet]);
              return;
            }

            if (Array.isArray(importedData)) {
              const newFleets = importedData.map((fleet: any) => ({
                ...fleet,
                id: `fleet-${Date.now()}-${Math.random()}`,
                vehicles: fleet.vehicles.map((vehicle: any) => ({
                  ...vehicle,
                  id: `vehicle-${Date.now()}-${Math.random()}`
                }))
              }));
              resolve(newFleets);
              return;
            }

            if (!importedData.vehicles || !Array.isArray(importedData.vehicles)) {
              throw new Error('Invalid fleet format: missing vehicles array');
            }
            const newFleet = {
              ...importedData,
              id: `fleet-${Date.now()}-${Math.random()}`,
              vehicles: importedData.vehicles.map((vehicle: any) => ({
                ...vehicle,
                id: `vehicle-${Date.now()}-${Math.random()}`
              }))
            };
            resolve([newFleet]);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = () => reject(new Error('Error reading file'));
        reader.readAsText(file);
      }),
    []
  );

  /**
   * Unified import handler: accepts JSON and Excel/CSV files. Excel/CSV can include Start/End Lat/Lon;
   * when present, start/end locations are created on the map via getOrCreatePointAtCoordinates.
   * Excel/CSV point creation is batched (one setLayers call) so all points appear; otherwise React
   * would batch multiple setLayers from the sync loop and only the last would apply.
   */
  const handleUnifiedFleetImport = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;
      const allNewFleets: Fleet[] = [];
      const supportedExtensions = ['.json', '.xlsx', '.xls', '.csv'];
      let validExtensionCount = 0;
      let failedCount = 0;

      for (const file of files) {
        const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
        if (!supportedExtensions.includes(ext)) {
          continue;
        }
        validExtensionCount++;
        try {
          if (ext === '.json') {
            const fleets = await processJsonFleetFile(file);
            if (fleets.length > 0) {
              allNewFleets.push(...fleets);
            } else {
              failedCount++;
            }
          } else if (ext === '.xlsx' || ext === '.xls') {
            const pointsToCreate: Array<{ point: Point; layerId: number }> = [];
            const options = {
              getPointId: (lat: number, lng: number, name: string) =>
                getOrCreatePointAtCoordinates(lat, lng, name, pointsToCreate),
            };
            const fleet = await parseExcelFile(file, options);
            if (fleet.vehicles.length > 0) {
              allNewFleets.push(fleet);
              if (pointsToCreate.length > 0 && context?.global?.setLayers) {
                context.global.setLayers((prev: Layer[]) => {
                  const updatedLayers = [...prev];
                  pointsToCreate.forEach(({ point, layerId }) => {
                    const layerIndex = updatedLayers.findIndex(l => l.layerId === layerId);
                    if (layerIndex >= 0) {
                      updatedLayers[layerIndex] = {
                        ...updatedLayers[layerIndex],
                        shapes: [...updatedLayers[layerIndex].shapes, point],
                      };
                    }
                  });
                  return updatedLayers;
                });
              }
            } else {
              failedCount++;
            }
          } else if (ext === '.csv') {
            const pointsToCreate: Array<{ point: Point; layerId: number }> = [];
            const options = {
              getPointId: (lat: number, lng: number, name: string) =>
                getOrCreatePointAtCoordinates(lat, lng, name, pointsToCreate),
            };
            const fleet = await parseCSVFile(file, options);
            if (fleet.vehicles.length > 0) {
              allNewFleets.push(fleet);
              if (pointsToCreate.length > 0 && context?.global?.setLayers) {
                context.global.setLayers((prev: Layer[]) => {
                  const updatedLayers = [...prev];
                  pointsToCreate.forEach(({ point, layerId }) => {
                    const layerIndex = updatedLayers.findIndex(l => l.layerId === layerId);
                    if (layerIndex >= 0) {
                      updatedLayers[layerIndex] = {
                        ...updatedLayers[layerIndex],
                        shapes: [...updatedLayers[layerIndex].shapes, point],
                      };
                    }
                  });
                  return updatedLayers;
                });
              }
            } else {
              failedCount++;
            }
          }
        } catch (error) {
          console.error(`Error importing ${file.name}:`, error);
          failedCount++;
          alert(t('fleetMenu.importAlerts.importError', { name: file.name, message: error instanceof Error ? error.message : t('fleetMenu.importAlerts.unknownError') }));
        }
      }

      if (allNewFleets.length > 0) {
        setFleets(prev => [...prev, ...allNewFleets]);
        setExpandedFleetId(allNewFleets[0].id);
        logEvent({
          category: 'Fleet Management',
          action: 'FleetImported',
          fleet_count: allNewFleets.length,
          source: 'unified_import'
        });
        if (failedCount > 0) {
          alert(t('fleetMenu.importAlerts.partialImport', { imported: allNewFleets.length, failed: failedCount }));
        }
      } else if (validExtensionCount === 0) {
        alert(t('fleetMenu.importAlerts.noFleetsExt'));
      } else {
        alert(t('fleetMenu.importAlerts.noFleetsFormat'));
      }
    },
    [processJsonFleetFile, setFleets, context?.global?.setLayers]
  );

  const onUnifiedImportInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      handleUnifiedFleetImport(Array.from(files));
      event.target.value = '';
    },
    [handleUnifiedFleetImport]
  );

  // Drag and drop: same unified logic for all accepted formats.
  const onDropFleetFiles = useCallback(
    (acceptedFiles: File[]) => {
      handleUnifiedFleetImport(acceptedFiles);
    },
    [handleUnifiedFleetImport]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropFleetFiles,
    onDragOver: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onDragEnter: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    onDragLeave: (e) => {
      e.preventDefault();
      e.stopPropagation();
    },
    accept: {
      'application/json': ['.json'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'text/csv': ['.csv'],
    },
    multiple: true,
    noClick: true,
    disabled: !open,
    noDragEventsBubbling: true,
  });

  const duplicateFleet = (fleet: Fleet, source: 'context_menu' | 'button') => {
    const duplicatedFleet: Fleet = {
      ...fleet,
      id: `fleet-${Date.now()}-${Math.random()}`,
      name: t('fleetMenu.copySuffix', { name: fleet.name }),
      vehicles: fleet.vehicles.map(vehicle => ({
        ...vehicle,
        id: `vehicle-${Date.now()}-${Math.random()}`
      }))
    };

    setFleets([...fleets, duplicatedFleet]);
    setExpandedFleetId(duplicatedFleet.id);

    logEvent({
      category: 'Fleet Management',
      action: 'FleetDuplicated',
      label: fleet.name,
      fleet_id: duplicatedFleet.id,
      source,
      vehicle_count: duplicatedFleet.vehicles.length,
    });
  };

  const handleAddOrEditVehicle = (vehicle: Vehicle) => {
    setFleets(prev => prev.map(fleet =>
      fleet.id === expandedFleetId
        ? {
            ...fleet,
            vehicles: editingVehicle
              ? fleet.vehicles.map(v => v.id === vehicle.id ? vehicle : v)
              : [...fleet.vehicles, vehicle]
          }
        : fleet
    ));
    
    // Track analytics events
    if (editingVehicle) {
      logEvent({
        category: 'Fleet Management',
        action: 'VehicleEdited',
        label: vehicle.name,
        value: vehicle.standardSeats + vehicle.wheelchairSeats,
        vehicle_id: vehicle.id,
        fleet_id: expandedFleetId,
        standard_seats: vehicle.standardSeats,
        wheelchair_seats: vehicle.wheelchairSeats,
        has_start_location: !!vehicle.startLocation,
        has_end_location: !!vehicle.endLocation,
        operating_periods_count: vehicle.operatingHours.length
      });
    } else {
      logEvent({
        category: 'Fleet Management',
        action: 'VehicleAdded',
        label: vehicle.name,
        value: vehicle.standardSeats + vehicle.wheelchairSeats,
        vehicle_id: vehicle.id,
        fleet_id: expandedFleetId,
        standard_seats: vehicle.standardSeats,
        wheelchair_seats: vehicle.wheelchairSeats,
        has_start_location: !!vehicle.startLocation,
        has_end_location: !!vehicle.endLocation,
        operating_periods_count: vehicle.operatingHours.length
      });
    }
    
    setVehicleModalOpen(false);
    setEditingVehicle(null);
    if (returnToOperationsFleetId) {
      setOperationsModalFleetId(returnToOperationsFleetId);
      setReturnToOperationsFleetId(null);
    }
  };

  const handleEditVehicle = (vehicle: Vehicle) => {
    logEvent({
      category: 'Fleet Management',
      action: 'VehicleEditInitiated',
      label: vehicle.name,
      vehicle_id: vehicle.id,
      fleet_id: expandedFleetId
    });
    setEditingVehicle(vehicle);
    setVehicleModalOpen(true);
  };

  const handleDeleteVehicle = (vehicleId: string) => {
    const vehicleToDelete = fleets.find(fleet => fleet.id === expandedFleetId)?.vehicles.find(v => v.id === vehicleId);
    
    logEvent({
      category: 'Fleet Management',
      action: 'VehicleDeleted',
      label: vehicleToDelete?.name || 'Unknown',
      value: vehicleToDelete ? vehicleToDelete.standardSeats + vehicleToDelete.wheelchairSeats : 0,
      vehicle_id: vehicleId,
      fleet_id: expandedFleetId,
      standard_seats: vehicleToDelete?.standardSeats || 0,
      wheelchair_seats: vehicleToDelete?.wheelchairSeats || 0
    });
    
    setFleets(prev => prev.map(fleet =>
      fleet.id === expandedFleetId
        ? { ...fleet, vehicles: fleet.vehicles.filter(v => v.id !== vehicleId) }
        : fleet
    ));
  };

  const handleAddFleet = () => {
    const newFleet = {
      id: `fleet-${Date.now()}`,
      name: newFleetName || t('fleetMenu.defaultFleetName', { index: fleets.length + 1 }),
      vehicles: [],
    };
    
    logEvent({
      category: 'Fleet Management',
      action: 'FleetAdded',
      label: newFleet.name,
      fleet_id: newFleet.id
    });
    
    setFleets([...fleets, newFleet]);
    setNewFleetName('');
  };

  const handleFleetNameChange = (fleetId: string) => {
    return (newName: string) => {
      const fleet = fleets.find(f => f.id === fleetId);
      if (fleet && fleet.name !== newName) {
        setFleets(prev => prev.map(f => 
          f.id === fleetId ? { ...f, name: newName } : f
        ));
        logEvent({
          category: 'Fleet Management',
          action: 'FleetNameChanged',
          label: newName,
          fleet_id: fleetId,
          previous_name: fleet.name,
          new_name: newName
        });
      }
    };
  };

  const handleDeleteFleet = (fleetId: string) => {
    setFleets(prev => prev.filter(f => f.id !== fleetId));
    if (fleetId === expandedFleetId && fleets.length > 1) {
      const nextFleet = fleets.find(f => f.id !== fleetId);
      if (nextFleet) {
        setExpandedFleetId(nextFleet.id);
        setSelectedFleetId(nextFleet.id);
      }
    }
  };

  const handleUpdateVehicle = (updatedVehicle: Vehicle) => {
    setFleets(prev => prev.map(fleet =>
      fleet.id === operationsModalFleetId
        ? {
            ...fleet,
            vehicles: fleet.vehicles.map(v => v.id === updatedVehicle.id ? updatedVehicle : v)
          }
        : fleet
    ));
  };

  const handleFleetOperations = (fleetId: string) => {
    const fleet = fleets.find(f => f.id === fleetId);
    logEvent({
      category: 'Fleet Management',
      action: 'FleetOperationsOpened',
      label: fleet?.name || 'Unknown Fleet',
      fleet_id: fleetId,
      vehicle_count: fleet?.vehicles.length || 0
    });
    setOperationsModalFleetId(fleetId);
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant="persistent" // Use persistent variant to allow map interaction (no Modal wrapper)
      PaperProps={{ 
        sx: (theme) => ({
          width: 400,
          maxWidth: 'calc(100vw - 65px)',
          p: 2,
          pr: 7, // Add right padding to prevent overlap with drawer buttons
          height: 'calc(100vh - 80px)', // Adjust height to account for logo
          top: 80, // Position below logo
          right: 65,
          zIndex: 2201, // Ensure drawer is above right rail strip
          ...frostedDrawerPaperSx(theme),
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
        })
      }}
    >
      {/* Drawer content with drag and drop support */}
      <Box
        {...getRootProps()}
        sx={{
          height: 'auto',
          minHeight: '100%',
          display: 'block',
          position: 'relative',
          pr: 0.5,
          pb: 2,
        }}
      >
        <input {...getInputProps()} />

        {/* Drag and Drop Overlay */}
        {isDragActive && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: 'rgba(25, 118, 210, 0.15)',
              border: '3px dashed',
              borderColor: 'primary.main',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1301,
              backdropFilter: 'blur(2px)',
            }}
          >
            <Paper
              sx={{
                p: 3,
                textAlign: 'left',
                bgcolor: 'background.paper',
                boxShadow: 6,
                borderRadius: 2,
              }}
            >
              <img 
                src="/icons/LiftangoLogo.png" 
                alt="Liftango Logo" 
                style={{ 
                  maxWidth: '200px', 
                  width: 'auto', 
                  height: 'auto', 
                  margin: '0 auto 16px', 
                  display: 'block'
                }} 
              />
              <Typography variant="h6" gutterBottom>
                {t('fleetMenu.dropTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('fleetMenu.dropSubtitle')}
              </Typography>
            </Paper>
          </Box>
        )}

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>{t('fleetMenu.title')}</Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label={t('fleetMenu.closeAria')}
          sx={(theme) => ({
            color: opsColors.neutral.inkSoft,
            borderRadius: 2,
            '&:hover': { backgroundColor: opsColors.neutral.panelAlt, color: opsColors.neutral.ink },
          })}
        >
          <Icon name="xmark" size={20} />
        </IconButton>
      </Box>
      
      {/* Fleet Presets Section */}
      {onFleetPresetSelectionChange && rides.length > 0 && (
        <Accordion
          expanded={showPresetSection}
          onChange={(_, expanded) => {
            setShowPresetSection(expanded);
          }}
          sx={(theme) => ({ mb: 2, ...drawerCardSx(theme) })}
        >
          <AccordionSummary 
            expandIcon={<Icon size={20} name="chevron-down" />}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon size={20} d={ICON_FALLBACKS.AutoAwesome} />
              <Typography variant="subtitle1">
                {t('fleetMenu.presets.title')}
              </Typography>
              {selectedFleetPresets.length > 0 && (
                <Chip
                  label={t('fleetMenu.presets.selectedCount', { count: selectedFleetPresets.length })}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 1.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('fleetMenu.presets.description')}
            </Typography>

            {/* Preset Selection */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('fleetMenu.presets.presetsHeading')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {FLEET_PRESETS.map((preset) => (
                  <Button
                    key={preset.type}
                    variant={selectedFleetPresets.includes(preset.type) ? 'contained' : 'outlined'}
                    onClick={() => toggleFleetPreset(preset.type)}
                    size="small"
                    sx={(theme) => segmentedControlButtonSx(theme, selectedFleetPresets.includes(preset.type))}
                  >
                    {preset.name}
                    {preset.vehicleAdjustment !== 0 && (
                      <Chip 
                        label={preset.vehicleAdjustment > 0 ? `+${preset.vehicleAdjustment}` : `${preset.vehicleAdjustment}`}
                        size="small" 
                        sx={{ ml: 1, height: '20px' }}
                        color={
                          preset.type === 'auto' ? 'default' : 
                          preset.type === 'savvy' ? 'success' : 'primary'
                        }
                      />
                    )}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Intelligent Generation */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('fleetMenu.presets.generateHeading')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {FLEET_PRESETS.map((preset) => (
                  <Button
                    key={`generate-${preset.type}`}
                    variant="outlined"
                    onClick={() => generateFleetWithPresetHandler(preset.type)}
                    size="small"
                    sx={(theme) => drawerSecondaryActionSx(theme)}
                  >
                    {t('fleetMenu.presets.generateButton', { name: preset.name })}
                  </Button>
                ))}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Auto Fleet Generator Button */}
      <Button
        variant="contained"
        color="primary"
        startIcon={<Icon size={20} d={ICON_FALLBACKS.AutoAwesome} />}
        fullWidth
        sx={(theme) => ({
          mb: 1,
          py: 1.1,
          fontSize: '1rem',
          boxShadow: opsElevation.raised.shadow,
          ...drawerPrimaryActionSx(theme),
        })}
        onClick={() => {
          setAutoFleetGeneratorOpen(true);
          logEvent({
            category: 'Fleet Management',
            action: 'AutoFleetGeneratorOpened',
            label: 'FleetMenu',
          });
        }}
      >
        {t('fleetMenu.autoGenerate')}
      </Button>

      <Button
        variant="outlined"
        fullWidth
        disabled={syncingToRostering || !fleets.some((f) => (f.vehicles || []).length > 0)}
        sx={(theme) => ({
          mb: 2,
          py: 1,
          ...drawerSecondaryActionSx(theme),
        })}
        onClick={() => {
          void (async () => {
            setSyncingToRostering(true);
            try {
              const seeds = flattenFleetsToVehicleTagSeeds(fleets);
              for (const seed of seeds) {
                await upsertVehicleTags(seed.vehicle_id, seed.capability_tags);
              }
              logEvent({
                category: 'Fleet Management',
                action: 'SyncToRostering',
                label: String(seeds.length),
              });
              navigate('/rules-engine?tab=vehicles');
            } catch (err) {
              // eslint-disable-next-line no-alert
              window.alert(
                err instanceof Error
                  ? err.message
                  : 'Failed to sync fleet vehicles to Rostering (need rules_engine_admin).'
              );
            } finally {
              setSyncingToRostering(false);
            }
          })();
        }}
        data-testid="fleet.sync-to-rostering"
      >
        {syncingToRostering ? 'Syncing…' : 'Sync vehicles to Rostering'}
      </Button>

      <Stack direction="row" spacing={1} mb={2} alignItems="center">
        <TextField
          size="small"
          label={t('fleetMenu.newFleetName')}
          value={newFleetName}
          onChange={e => setNewFleetName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleAddFleet(); }}
          sx={{ flex: 1 }}
        />
        <Button
          variant="contained"
          onClick={handleAddFleet}
          aria-label={t('fleetMenu.addFleetAria')}
          sx={(theme) => ({
            minWidth: 44,
            width: 44,
            height: 40,
            p: 0,
            ...drawerPrimaryActionSx(theme),
          })}
        >
          <Icon name="plus" size={20} />
        </Button>
      </Stack>
      <Box>
        {fleets.map(fleet => (
          <Accordion
            key={fleet.id}
            expanded={expandedFleetId === fleet.id}
            onChange={() => {
              setExpandedFleetId(expandedFleetId === fleet.id ? '' : fleet.id);
              setSelectedFleetId(fleet.id);
            }}
            sx={(theme) => ({
              mb: 1.5,
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${expandedFleetId === fleet.id ? opsColors.brand.primary : opsColors.neutral.line}`,
              boxShadow: expandedFleetId === fleet.id ? opsElevation.floating.shadow : opsElevation.raised.shadow,
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: '0 0 12px 0' },
            })}
          >
            <AccordionSummary
              component="div"
              expandIcon={<Icon size={20} name="chevron-down" />}
              aria-controls={`fleet-content-${fleet.id}`}
              id={`fleet-header-${fleet.id}`}
              onContextMenu={(e) => {
                e.preventDefault();
                setFleetContextMenu({ x: e.pageX, y: e.pageY, fleet });
              }}
              sx={(theme) => ({
                backgroundColor: opsColors.neutral.panel,
                display: 'flex',
                alignItems: 'center',
                minHeight: 62,
                '& .MuiAccordionSummary-content': { my: 1 },
              })}
            >
              <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
                  <EditableTypography 
                    variant="h6" 
                    setValueFunction={handleFleetNameChange(fleet.id)}
                  >
                    {fleet.name}
                  </EditableTypography>
                  {expandedFleetId !== fleet.id && (
                    <Typography variant="body2" color="text.secondary">
                      {t('fleetMenu.vehiclesCount', { count: fleet.vehicles.length })}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    edge="end"
                    aria-label={t('fleetMenu.exportFleetAria')}
                    onClick={e => {
                      e.stopPropagation();
                      setExportDialogFleetId(fleet.id);
                    }}
                    title={t('fleetMenu.exportFleetTooltip')}
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Icon name="file-arrow-down" size={20} />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label={t('fleetMenu.duplicateFleetAria')}
                    onClick={e => { e.stopPropagation(); duplicateFleet(fleet, 'button'); }}
                    title={t('fleetMenu.duplicateFleetTooltip')}
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Icon name="clipboard" size={20} />
                  </IconButton>
                  <IconButton
                    edge="end"
                    aria-label={t('fleetMenu.fleetOperationsAria')}
                    data-testid="map.fleet-menu.fleet-operations"
                    onClick={e => { e.stopPropagation(); handleFleetOperations(fleet.id); }}
                    size="small"
                    sx={{ color: 'text.secondary' }}
                  >
                    <Icon d={ICON_FALLBACKS.Timeline} size={20} />
                  </IconButton>
                  {fleets.length > 1 && (
                    <IconButton
                      edge="end"
                      aria-label={t('fleetMenu.deleteFleetAria')}
                      onClick={e => { e.stopPropagation(); handleDeleteFleet(fleet.id); }}
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      <Icon name="trash" size={20} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ position: 'relative', pb: 6 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1">{t('fleetMenu.vehiclesHeading')}</Typography>
                <Button
                  variant="text"
                  startIcon={<Icon size={20} d={ICON_FALLBACKS.PlaylistAdd} />}
                  onClick={() => { setVehicleModalOpen(true); setEditingVehicle(null); setExpandedFleetId(fleet.id); setSelectedFleetId(fleet.id); }}
                  sx={(theme) => ({
                    ...drawerTertiaryActionSx(theme),
                    color: opsColors.brand.primary,
                    fontWeight: 600,
                  })}
                >
                  {t('fleetMenu.addVehicle')}
                </Button>
              </Box>
              <List
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                {fleet.vehicles.map(vehicle => (
                  <ListItem
                    key={vehicle.id}
                    dense
                    disableGutters
                    alignItems="center"
                    secondaryAction={
                      <IconButton
                        size="small"
                        aria-label={t('fleetMenu.deleteVehicleAria')}
                        onClick={() => {
                          setExpandedFleetId(fleet.id);
                          setSelectedFleetId(fleet.id);
                          handleDeleteVehicle(vehicle.id);
                        }}
                        sx={{ mr: -0.5 }}
                      >
                        <Icon name="trash" size={20} />
                      </IconButton>
                    }
                    sx={(theme) => ({
                      py: 0.25,
                      pl: 0,
                      pr: 4.5,
                      '& .MuiListItemSecondaryAction-root': {
                        right: theme.spacing(0.25),
                      },
                    })}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, pr: 0.5 }}>
                      <IconButton
                        size="small"
                        aria-label={t('fleetMenu.editVehicleAria')}
                        onClick={() => {
                          setExpandedFleetId(fleet.id);
                          setSelectedFleetId(fleet.id);
                          handleEditVehicle(vehicle);
                        }}
                        sx={(theme) => ({
                          ml: -0.75,
                          mr: 0.25,
                          flexShrink: 0,
                          color: opsColors.neutral.inkSoft,
                          '&:hover': {
                            backgroundColor: opsColors.neutral.panelAlt,
                            color: opsColors.neutral.ink,
                          },
                        })}
                      >
                        <Icon name="pen-to-square" size={20} />
                      </IconButton>
                      <Icon size={20} name="car-side" style={{ marginRight: 6, flexShrink: 0, color: '#52565e' }} />
                      <ListItemText
                        primary={vehicle.name}
                        secondary={t('fleetMenu.seatsSummary', { standard: vehicle.standardSeats, wheelchair: vehicle.wheelchairSeats })}
                        sx={{ m: 0, minWidth: 0 }}
                      />
                    </Box>
                  </ListItem>
                ))}
                {fleet.vehicles.length === 0 && (
                  <ListItem>
                    <ListItemText primary={t('fleetMenu.noVehiclesInFleet')} />
                  </ListItem>
                )}
              </List>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>

      {/* Bulk Operations Section */}
      <Accordion
        expanded={showBulkOperationsSection}
        onChange={(_, expanded) => {
          setShowBulkOperationsSection(expanded);
        }}
        sx={(theme) => ({ mt: 2, mb: 2, ...drawerCardSx(theme) })}
      >
        <AccordionSummary
          expandIcon={<Icon size={20} name="chevron-down" />}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Icon size={20} name="file-arrow-up" />
            <Typography variant="subtitle1">
              {t('fleetMenu.bulkOps.title')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('fleetMenu.bulkOps.description')}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'nowrap', alignItems: 'stretch' }}>
            <Button
              variant="contained"
              startIcon={<Icon size={20} name="file-arrow-up" />}
              component="label"
              size="small"
              color="primary"
              title={t('fleetMenu.bulkOps.importFleetTooltip')}
              sx={(theme) => ({ flex: 1, minWidth: 0, whiteSpace: 'nowrap', ...drawerPrimaryActionSx(theme) })}
            >
              {t('fleetMenu.bulkOps.importFleet')}
              <input
                type="file"
                accept=".json,.xlsx,.xls,.csv"
                multiple
                hidden
                onChange={onUnifiedImportInputChange}
              />
            </Button>
            <Button
              variant="outlined"
              startIcon={<Icon size={20} name="file-arrow-down" />}
              onClick={exportAllFleetsToJSON}
              disabled={fleets.length === 0}
              size="small"
              sx={(theme) => ({ flex: 1, minWidth: 0, whiteSpace: 'nowrap', ...drawerSecondaryActionSx(theme) })}
            >
              {t('fleetMenu.bulkOps.exportAll')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                if (window.confirm(t('fleetMenu.bulkOps.confirmClearAll'))) {
                  setFleets([]);
                  logEvent({
                    category: 'Fleet Management',
                    action: 'AllFleetsCleared'
                  });
                }
              }}
              disabled={fleets.length === 0}
              size="small"
              color="error"
              sx={(theme) => ({ flex: 1, minWidth: 0, whiteSpace: 'nowrap', ...drawerDestructiveActionSx(theme) })}
            >
              {t('fleetMenu.bulkOps.clearAll')}
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Export format dialog */}
      <Dialog open={exportDialogFleetId !== null} onClose={() => setExportDialogFleetId(null)} maxWidth="xs" fullWidth sx={{ zIndex: 2500 }}>
        <DialogTitle>{t('fleetMenu.exportDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {exportDialogFleetId ? fleets.find(f => f.id === exportDialogFleetId)?.name : ''}
          </Typography>
          <Stack spacing={1}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<Icon size={20} name="file-arrow-down" />}
              sx={(theme) => drawerSecondaryActionSx(theme)}
              onClick={() => {
                const fleet = exportDialogFleetId ? fleets.find(f => f.id === exportDialogFleetId) : null;
                if (fleet) {
                  const validPoints = getValidPoints(context?.global?.mapLayers?.flatMap(layer => layer.shapes.filter(s => s.type === 'point')) || []);
                  exportFleetToExcel(fleet, validPoints);
                  logEvent({ category: 'Fleet Management', action: 'FleetExportedToExcel', label: fleet.name, fleet_id: fleet.id });
                }
                setExportDialogFleetId(null);
              }}
            >
              {t('fleetMenu.exportDialog.excel')}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<Icon size={20} name="file-arrow-down" />}
              sx={(theme) => drawerSecondaryActionSx(theme)}
              onClick={() => {
                const fleet = exportDialogFleetId ? fleets.find(f => f.id === exportDialogFleetId) : null;
                if (fleet) {
                  const validPoints = getValidPoints(context?.global?.mapLayers?.flatMap(layer => layer.shapes.filter(s => s.type === 'point')) || []);
                  exportFleetToCSV(fleet, validPoints);
                  logEvent({ category: 'Fleet Management', action: 'FleetExportedToCSV', label: fleet.name, fleet_id: fleet.id });
                }
                setExportDialogFleetId(null);
              }}
            >
              {t('fleetMenu.exportDialog.csv')}
            </Button>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<Icon size={20} d={ICON_FALLBACKS.Code} />}
              sx={(theme) => drawerSecondaryActionSx(theme)}
              onClick={() => {
                const fleet = exportDialogFleetId ? fleets.find(f => f.id === exportDialogFleetId) : null;
                if (fleet) {
                  exportFleetToJSON(fleet);
                  logEvent({ category: 'Fleet Management', action: 'FleetExported', label: fleet.name, fleet_id: fleet.id });
                }
                setExportDialogFleetId(null);
              }}
            >
              {t('fleetMenu.exportDialog.json')}
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogFleetId(null)} sx={(theme) => drawerSecondaryActionSx(theme)}>{t('common:cancel')}</Button>
        </DialogActions>
      </Dialog>

      <FleetContextMenu
        contextMenu={fleetContextMenu}
        onClose={() => setFleetContextMenu(null)}
        onDuplicate={(fleet) => duplicateFleet(fleet, 'context_menu')}
      />
      <FleetVehicleModal
        open={vehicleModalOpen}
        onClose={() => {
          setVehicleModalOpen(false);
          setEditingVehicle(null);
          if (returnToOperationsFleetId) {
            setOperationsModalFleetId(returnToOperationsFleetId);
            setReturnToOperationsFleetId(null);
          }
        }}
        onSave={handleAddOrEditVehicle}
        vehicle={editingVehicle}
        points={allPoints}
        defaultName={t('fleetMenu.defaultVehicleName', { index: ((fleets.find(f => f.id === selectedFleetId)?.vehicles.length) ?? 0) + 1 })}
      />
      <FleetOperationsModal
        open={!!operationsModalFleetId}
        onClose={() => setOperationsModalFleetId(null)}
        fleetName={fleets.find(f => f.id === operationsModalFleetId)?.name || ''}
        vehicles={fleets.find(f => f.id === operationsModalFleetId)?.vehicles || []}
        onEditVehicle={(vehicle) => {
          setOperationsModalFleetId(null);
          setEditingVehicle(vehicle);
          setVehicleModalOpen(true);
          setReturnToOperationsFleetId(operationsModalFleetId);
        }}
        onAddVehicle={(vehicle) => {
          setFleets(prev => prev.map(fleet =>
            fleet.id === operationsModalFleetId
              ? { ...fleet, vehicles: [...fleet.vehicles, vehicle] }
              : fleet
          ));
        }}
        onUpdateVehicle={handleUpdateVehicle}
        onDeleteVehicle={handleDeleteVehicle}
        points={allPoints}
      />

      </Box>
    </Drawer>

    {/* Auto Fleet Generator Modal - rendered outside Drawer to avoid stacking context issues */}
    <AutoFleetGeneratorModal
      open={autoFleetGeneratorOpen}
      onClose={() => setAutoFleetGeneratorOpen(false)}
      onFleetCreated={(fleetName, vehicles) => {
        const newFleet: Fleet = {
          id: `fleet-${Date.now()}`,
          name: fleetName,
          vehicles: vehicles,
        };

        setFleets(prev => [...prev, newFleet]);
        setExpandedFleetId(newFleet.id);
        setSelectedFleetId(newFleet.id);
        setAutoFleetGeneratorOpen(false);

        logEvent({
          category: 'Fleet Management',
          action: 'AutoGeneratedFleetCreated',
          label: fleetName,
          value: vehicles.length,
          vehicle_count: vehicles.length,
        });
      }}
    />
    </>
  );
};

export default FleetMenu; 